import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AddDomainDialog = ({ onDomainAdded }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    domain_name: '',
    tags: '',
    notes: '',
    manual_expiration_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        domain_name: formData.domain_name,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        notes: formData.notes,
        manual_expiration_date: formData.manual_expiration_date || null
      };

      const response = await axios.post(`${API_URL}/domains`, payload);
      
      if (!response.data.expiration_date && !response.data.registrar) {
        toast.warning('Domain added, but WHOIS data unavailable. Some TLDs (.ch, .de, etc.) require premium API access.');
      } else {
        toast.success('Domain added successfully!');
      }
      
      setOpen(false);
      setFormData({ domain_name: '', tags: '', notes: '', manual_expiration_date: '' });
      onDomainAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all duration-300"
          data-testid="add-domain-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Domain
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="add-domain-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add New Domain</DialogTitle>
          <DialogDescription className="font-body">
            Enter domain details to start monitoring
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="domain" className="text-xs font-mono uppercase tracking-widest">
              Domain Name
            </Label>
            <Input
              id="domain"
              placeholder="example.com"
              value={formData.domain_name}
              onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
              required
              disabled={loading}
              className="font-mono"
              data-testid="domain-name-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Supported: .com, .net, .org, .io, .co, .us, .uk • Premium required: .ch, .de, .fr
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tags" className="text-xs font-mono uppercase tracking-widest">
              Tags (comma-separated)
            </Label>
            <Input
              id="tags"
              placeholder="personal, client, important"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              disabled={loading}
              className="font-mono"
              data-testid="tags-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiration" className="text-xs font-mono uppercase tracking-widest">
              Manual Expiration Date (Optional)
            </Label>
            <Input
              id="expiration"
              type="date"
              value={formData.manual_expiration_date}
              onChange={(e) => setFormData({ ...formData, manual_expiration_date: e.target.value })}
              disabled={loading}
              className="font-mono"
              data-testid="expiration-date-input"
            />
            <p className="text-xs text-muted-foreground">
              Only needed if WHOIS data is unavailable
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-xs font-mono uppercase tracking-widest">
              Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Optional notes about this domain"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={loading}
              className="font-body"
              data-testid="notes-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="submit-domain-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              'Add Domain'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};